import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SouthGloucestershireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="South Gloucestershire Pothole Claims | £12m Funding Gap | Fixtyer"
        description="South Gloucestershire Council reports a £12 million annual highways funding shortfall and filled 12,148 potholes in 2024/25 — more than double the 2020/21 figure. See what the DfT AMBER rating means for your claim."
        canonical="/councils/south-gloucestershire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'South Gloucestershire', url: '/councils/south-gloucestershire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'South Gloucestershire has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because the council projects spending £11.773m capital against a £10.4m DfT allocation in 2025/26. But Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate budgets. The council\'s own report identifies a £12 million per annum shortfall to achieve its desired maintenance strategy, and 17% of unclassified roads remain in RED condition.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 963.9km — roughly two-thirds of South Gloucestershire\'s 1,482.7km network. At the latest survey, 17% of U-roads were in RED condition (approximately 164km of estate roads, village lanes and rural routes). The council surveys 100% of classified roads one year and 100% of the unclassified network the following year, so your road may not have been condition-surveyed in the year of your incident.',
          },
          {
            question: 'Does the improvement from 22% to 17% RED U-roads weaken my claim?',
            answer: 'Not necessarily. The council itself notes that the lower category of roads will always show a worse condition rating due to the length of the UC network and lower priority. Rural lanes remain "problematic and often associated with poorer drainage infrastructure." One in six U-roads is still in RED — and the council admits that without the £12m annual funding gap being closed, the network is "always likely to deteriorate year on year."',
          },
          {
            question: 'Pothole repairs more than doubled since 2020/21 — does that mean the roads are fixed?',
            answer: 'No. South Gloucestershire filled 4,785 potholes in 2020/21 and 12,148 in 2024/25 — a rise the council links to two very bad winters and prolonged wet weather. The authority reduced early intervention treatments to address a backlog of late-intervention resurfacing sites. Reactive repairs treat symptoms; they do not prove the specific defect that damaged your car was caught in time.',
          },
          {
            question: 'Why is Best Practice AMBER if spend is GREEN?',
            answer: 'The DfT scores condition, spend and best practice separately before combining them into an overall AMBER rating. Best Practice measures documented processes, asset management evidence and data submission. South Gloucestershire is moving U-roads from visual inspection towards SCANNER surveys and will adopt PAS 2161 from 2026/27 — transitions that suggest the current data regime has room for improvement.',
          },
          {
            question: 'How does Section 41 of the Highways Act 1980 apply here?',
            answer: 'Section 41 imposes a duty on South Gloucestershire Council, as highway authority, to maintain adopted highways. Section 58 provides a defence if the council can prove it took such care as was reasonable to secure that the part of the highway in question was not dangerous. Your claim succeeds when you show the defect was dangerous, caused your damage, and the council either knew or ought to have known about it — regardless of GREEN spend scorecards.',
          },
        ]}
      />
      <ServiceSchema
        councilName="South Gloucestershire Council"
        councilSlug="south-gloucestershire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/south-gloucestershire"
        headline="South Gloucestershire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">South Gloucestershire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            South Gloucestershire: £12m Shortfall, Pothole Repairs More Than Doubled
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            South Gloucestershire Council earns a <strong className="text-emerald-400">GREEN</strong> spend scorecard
            and projects £11.773m capital against a £10.4m DfT allocation — yet its own transparency report identifies
            a <strong className="text-rose-400">£12 million per annum funding shortfall</strong>,{' '}
            <strong className="text-rose-400">12,148 pothole repairs in 2024/25</strong> (up from 4,785 in 2020/21),
            and an overall <strong className="text-amber-400">AMBER</strong> DfT rating because condition and best
            practice lag behind the budget headline.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">£12m</div>
            <div className="text-white font-medium mt-2">Annual funding shortfall</div>
            <div className="text-brand-300 text-sm mt-1">
              The council&apos;s own backlog report says £12 million more per year is needed to achieve its desired
              strategy — without it, the network is &quot;always likely to deteriorate year on year.&quot;
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and visual survey data from South Gloucestershire&apos;s own transparency report —
            A-roads broadly flat, B/C roads edging worse, U-roads improved but still one in six in RED
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (112.5km — 7.6% of network): broadly stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 2% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">18%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 19-20%</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">79%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">flat at 77-79%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              The council notes A-roads show a &quot;slight decline in condition over the last 5 years&quot; and that
              the most recent survey trend is to halt that decline — including major A38 Filton flyover works. A-roads
              are a small slice of a network dominated by local routes.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (406.3km — 27.4% of network): RED edging up</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">30%</td>
                    <td className="px-4 py-2 text-center">63%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">30%</td>
                    <td className="px-4 py-2 text-center">63%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8%</td>
                    <td className="px-4 py-2 text-center font-bold">30%</td>
                    <td className="px-4 py-2 text-center font-bold">62%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads rose from 7% to 8% in 2024, and green roads fell back to 62%. Combined with
              amber, roughly 38% of B and C roads now need — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend, AMBER reality</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£10.4m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£11.8m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">84%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share (2025/26)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              South Gloucestershire spends above its DfT allocation — yet the council&apos;s own backlog report
              identifies a <span className="text-rose-400">£12 million per annum shortfall</span> to achieve
              nominal improvement. GREEN on spend does not mean the network is fully funded.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Two-thirds of the network: 963.9km of U-roads
          </h2>
          <p className="text-surface-600 mb-8">
            Unclassified roads make up roughly two-thirds of South Gloucestershire&apos;s highway network — surveyed
            in alternate years on a visual basis, moving towards SCANNER
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The alternate-year survey cycle</h3>
              <p className="text-surface-600 text-sm mb-3">
                South Gloucestershire surveys 100% of classified roads (A, B and C) one year and 100% of the
                unclassified network the following year. U-roads have been surveyed by visual inspection, though
                the council is moving towards SCANNER-type surveys for consistency.
              </p>
              <p className="text-surface-600 text-sm">
                At 17% RED, approximately <strong>164km of U-roads</strong> need maintenance considered — estate
                roads around Yate, Thornbury, Patchway and rural lanes across the district.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Rural drainage compounding surface defects</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council states its rural network is &quot;a particular part of the UC network where the condition
                is problematic and often associated with poorer drainage infrastructure.&quot; It is not just the road
                surface that needs addressing but the drainage as well.
              </p>
              <p className="text-amber-800 text-sm">
                Flooding events have increased in recent years, and prolonged wet weather has driven a dramatic rise
                in pothole numbers — surface water and poor drainage are directly linked to defect formation.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For South Gloucestershire&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road condition-surveyed in the year of your incident — or was it an off-year for U-roads?</li>
              <li>• If 17% of U-roads were RED at the last survey, what was done about yours specifically?</li>
              <li>• Does visual inspection catch defects between formal survey years on rural routes with poor drainage?</li>
              <li>• How will the move to PAS 2161 from 2026/27 affect comparability of pre-existing records?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a network it surveys on alternate years — using a different
              methodology on U-roads than on classified routes.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repair surge */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            43,136 pothole repairs in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — more than doubling since 2020/21 as winters and wet weather took their toll
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive maintenance share</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">4,785</td>
                  <td className="px-6 py-4 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">5,223</td>
                  <td className="px-6 py-4 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">8,738</td>
                  <td className="px-6 py-4 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12,242</td>
                  <td className="px-6 py-4 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12,148</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">43,136</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~24 repairs a day, every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, South Gloucestershire fills around 24 potholes per day. The council
                anticipates around 10,000 repairs in 2025/26 — still more than double the 2020/21 baseline. A
                network producing defects at this rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Early intervention sacrificed</h3>
              <p className="text-amber-800 text-sm">
                The council states that two very bad winters led to &quot;reducing the amount of early intervention
                treatments to address a backlog of serious later intervention resurfacing sites.&quot; Reactive share
                rose to 21% in 2024/25 — the highest in five years — as the authority shifted from prevention to
                catch-up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The winter and weather admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Two bad winters and a wet-weather spike
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s own explanation for rising pothole numbers — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The ageing network and two very bad winters have led to a couple of years of challenging choices. This has meant reducing the amount of early intervention treatments to address a backlog of serious later intervention resurfacing sites. Pothole numbers and public satisfaction surveys have reflected the winters effects, and additional resources have been allocated accordingly."
              source="South Gloucestershire Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="The prolonged periods of very wet weather that we have experienced over the last few years has seen a dramatic increase in potholes and subsequent repairs."
              source="South Gloucestershire Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                South Gloucestershire formally acknowledges that weather events drove a dramatic rise in potholes
                and forced a shift from preventative to reactive maintenance. That is documented knowledge that
                defect formation outpaced the council&apos;s planned programme.
              </p>
              <p className="text-surface-600 text-sm">
                When a council knows winters and wet weather are accelerating deterioration, the standard for
                what a &quot;reasonable&quot; inspection frequency looks like rises — particularly on rural U-roads
                with known drainage problems.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Did the council increase inspection frequency after the bad winters?</li>
                <li>• Was your defect on a rural route the council identifies as drainage-problematic?</li>
                <li>• If early intervention was cut, why wasn&apos;t your pothole caught by reactive patrols?</li>
                <li>• Had anyone reported the same defect before your incident?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER council with GREEN spend
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: South Gloucestershire invests above its DfT allocation — here is how that shapes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 79-86% of maintenance spend classed as preventative over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented priority scoring using survey data, complaints and pothole records
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED condition improved from 22% to 17% since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Road Work Permit Scheme and 10-working-day inspection target for reported defects
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on main routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C RED roads rose to 8% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £12m annual funding shortfall — council admits network likely to deteriorate without it
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 17% of U-roads still in RED — roughly 164km of local routes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Alternate-year U-road survey cycle with visual inspection (not yet SCANNER)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 43,136 pothole repairs in five years — defects forming faster than prevention catches them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — methodology transitions underway, not yet complete
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but AMBER condition and best practice, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the alternate-year survey gap is your strongest structural argument</li>
              <li>• Location on rural routes where the council admits drainage infrastructure is problematic</li>
              <li>• Timing relative to the bad winters and wet-weather spike the council documents</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              South Gloucestershire&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to South Gloucestershire Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting creates a dated record the council cannot ignore — and prior reports strengthen Section 58
            rebuttals if you later claim for damage. The council aims to inspect reported defects within 10 working days.
          </p>
          <a
            href="https://beta.southglos.gov.uk/report-a-pothole-damaged-road-footpath-or-cycleway/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole to South Gloucestershire Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in South Gloucestershire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No alternate-year U-road survey argument</li>
                <li>• No £12m funding-gap context</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ £12m annual shortfall documented</li>
                <li>✅ Alternate-year U-road survey cycle argued</li>
                <li>✅ 43,136 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to South Gloucestershire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=south-gloucestershire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my South Gloucestershire claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                South Gloucestershire has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because the council projects spending £11.773m capital against
                a £10.4m DfT allocation in 2025/26. But Section 58 turns on whether the specific defect that damaged
                your vehicle was reasonably inspected and repaired — not on aggregate budgets. The council&apos;s own
                report identifies a £12 million per annum shortfall to achieve its desired maintenance strategy, and
                17% of unclassified roads remain in RED condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 963.9km — roughly two-thirds of South Gloucestershire&apos;s 1,482.7km
                network. At the latest survey, 17% of U-roads were in RED condition (approximately 164km of estate
                roads, village lanes and rural routes). The council surveys 100% of classified roads one year and
                100% of the unclassified network the following year, so your road may not have been condition-surveyed
                in the year of your incident.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the improvement from 22% to 17% RED U-roads weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. The council itself notes that the lower category of roads will always show a worse
                condition rating due to the length of the UC network and lower priority. Rural lanes remain
                &quot;problematic and often associated with poorer drainage infrastructure.&quot; One in six U-roads
                is still in RED — and the council admits that without the £12m annual funding gap being closed, the
                network is &quot;always likely to deteriorate year on year.&quot;
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs more than doubled since 2020/21 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. South Gloucestershire filled 4,785 potholes in 2020/21 and 12,148 in 2024/25 — a rise the council
                links to two very bad winters and prolonged wet weather. The authority reduced early intervention
                treatments to address a backlog of late-intervention resurfacing sites. Reactive repairs treat
                symptoms; they do not prove the specific defect that damaged your car was caught in time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Best Practice AMBER if spend is GREEN?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT scores condition, spend and best practice separately before combining them into an overall
                AMBER rating. Best Practice measures documented processes, asset management evidence and data
                submission. South Gloucestershire is moving U-roads from visual inspection towards SCANNER surveys and
                will adopt PAS 2161 from 2026/27 — transitions that suggest the current data regime has room for
                improvement.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 41 of the Highways Act 1980 apply here?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 41 imposes a duty on South Gloucestershire Council, as highway authority, to maintain adopted
                highways. Section 58 provides a defence if the council can prove it took such care as was reasonable
                to secure that the part of the highway in question was not dangerous. Your claim succeeds when you
                show the defect was dangerous, caused your damage, and the council either knew or ought to have known
                about it — regardless of GREEN spend scorecards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://beta.southglos.gov.uk/static/6b48849fec01463ac4798b3c1115e43e/Local_highways_maintenance_transparency_report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              South Gloucestershire Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
